<template>
  <Transition name="banner">
    <div v-if="showBanner" class="offline-banner" :class="bannerClass">
      <div class="banner-content">
        <span class="banner-icon">{{ statusIcon }}</span>
        <span class="banner-message">{{ statusMessage }}</span>
        <span v-if="pendingCount > 0" class="banner-pending">
          {{ t('banner.pending', { count: pendingCount }) }}
        </span>
      </div>
      <div class="banner-actions">
        <button
          v-if="isOnline && hasPending"
          class="banner-btn sync"
          @click="handleSync"
          :disabled="isSyncing"
        >
          <span v-if="isSyncing" class="spinner"></span>
          <span v-else>{{ t('banner.sync_now') }}</span>
        </button>
        <button
          v-if="dismissable"
          class="banner-btn dismiss"
          @click="handleDismiss"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

// Props
const props = withDefaults(
  defineProps<{
    isOnline: boolean
    isSyncing?: boolean
    pendingCount?: number
    conflictCount?: number
    lastSyncAt?: number | null
    dismissable?: boolean
    autoDismissDelay?: number
    translations?: Record<string, string>
  }>(),
  {
    isSyncing: false,
    pendingCount: 0,
    conflictCount: 0,
    lastSyncAt: null,
    dismissable: true,
    autoDismissDelay: 0,
  }
)

// Emits
const emit = defineEmits<{
  sync: []
  dismiss: []
}>()

// Internal state
const isDismissed = ref(false)
let autoDismissTimer: ReturnType<typeof setTimeout> | null = null

// Computed
const showBanner = computed(() => {
  if (isDismissed.value) return false
  // Show when offline or syncing or has pending
  return !props.isOnline || props.isSyncing || props.pendingCount > 0
})

const hasPending = computed(() => props.pendingCount > 0)

const bannerClass = computed(() => ({
  offline: !props.isOnline,
  syncing: props.isSyncing,
  pending: props.isOnline && hasPending.value && !props.isSyncing,
  'has-conflicts': props.conflictCount > 0,
}))

const statusIcon = computed(() => {
  if (!props.isOnline) return '📡'
  if (props.isSyncing) return '🔄'
  if (props.conflictCount > 0) return '⚠️'
  if (props.pendingCount > 0) return '⏳'
  return '✓'
})

const statusMessage = computed(() => {
  if (!props.isOnline) {
    return t('banner.offline')
  }
  if (props.isSyncing) {
    return t('banner.syncing')
  }
  if (props.conflictCount > 0) {
    return t('banner.conflicts', { count: props.conflictCount })
  }
  if (props.pendingCount > 0) {
    return t('banner.ready_to_sync')
  }
  return t('banner.online')
})

// Simple translation function
function t(key: string, params?: Record<string, string | number>): string {
  const translations: Record<string, string> = {
    'banner.offline': 'You are offline',
    'banner.online': 'You are online',
    'banner.syncing': 'Syncing...',
    'banner.ready_to_sync': 'Ready to sync',
    'banner.pending': '{count} changes pending',
    'banner.conflicts': '{count} conflicts to resolve',
    'banner.sync_now': 'Sync Now',
    ...props.translations,
  }

  let text = translations[key] || key
  if (params) {
    for (const [param, value] of Object.entries(params)) {
      text = text.replace(`{${param}}`, String(value))
    }
  }
  return text
}

// Handlers
function handleSync(): void {
  emit('sync')
}

function handleDismiss(): void {
  isDismissed.value = true
  emit('dismiss')
}

// Reset dismissed state when going offline
watch(
  () => props.isOnline,
  (online) => {
    if (!online) {
      isDismissed.value = false
    }
  }
)

// Auto-dismiss when coming back online and synced
watch(
  [() => props.isOnline, () => props.pendingCount, () => props.isSyncing],
  ([online, pending, syncing]) => {
    if (autoDismissTimer) {
      clearTimeout(autoDismissTimer)
      autoDismissTimer = null
    }

    if (online && pending === 0 && !syncing && props.autoDismissDelay > 0) {
      autoDismissTimer = setTimeout(() => {
        isDismissed.value = true
      }, props.autoDismissDelay)
    }
  }
)
</script>

<style scoped>
.offline-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  transition: all 0.3s ease;
}

.offline-banner.offline {
  background: var(--color-error-light, #fef2f2);
  color: var(--color-error, #dc2626);
  border-bottom: 2px solid var(--color-error, #dc2626);
}

.offline-banner.syncing {
  background: var(--color-info-light, #eff6ff);
  color: var(--color-info, #2563eb);
  border-bottom: 2px solid var(--color-info, #2563eb);
}

.offline-banner.pending {
  background: var(--color-warning-light, #fffbeb);
  color: var(--color-warning, #d97706);
  border-bottom: 2px solid var(--color-warning, #d97706);
}

.offline-banner.has-conflicts {
  background: var(--color-warning-light, #fffbeb);
  color: var(--color-warning-dark, #b45309);
  border-bottom: 2px solid var(--color-warning, #d97706);
}

.banner-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.banner-icon {
  font-size: 1rem;
}

.banner-message {
  font-weight: 500;
}

.banner-pending {
  font-weight: normal;
  opacity: 0.8;
}

.banner-pending::before {
  content: '•';
  margin: 0 0.5rem;
}

.banner-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.banner-btn {
  padding: 0.375rem 0.75rem;
  border-radius: 4px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.banner-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.banner-btn.sync {
  background: currentColor;
  color: white;
  min-width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
}

.offline-banner.offline .banner-btn.sync {
  background: var(--color-error, #dc2626);
}

.offline-banner.syncing .banner-btn.sync {
  background: var(--color-info, #2563eb);
}

.offline-banner.pending .banner-btn.sync {
  background: var(--color-warning, #d97706);
}

.banner-btn.sync:hover:not(:disabled) {
  opacity: 0.9;
}

.banner-btn.dismiss {
  background: transparent;
  padding: 0.25rem;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
}

.banner-btn.dismiss:hover {
  opacity: 1;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid transparent;
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Transitions */
.banner-enter-active,
.banner-leave-active {
  transition: all 0.3s ease;
}

.banner-enter-from,
.banner-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

/* Dark mode */
@media (prefers-color-scheme: dark) {
  .offline-banner.offline {
    background: #450a0a;
    color: #fca5a5;
    border-color: #dc2626;
  }

  .offline-banner.syncing {
    background: #1e3a5f;
    color: #93c5fd;
    border-color: #3b82f6;
  }

  .offline-banner.pending {
    background: #451a03;
    color: #fcd34d;
    border-color: #f59e0b;
  }
}
</style>
